import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Check, Zap, Crown, Loader2, ArrowLeft, Settings } from "lucide-react";

const FEATURES = {
  free: ["Match sites with up to 5K monthly visits", "5 swipes per day", "1 match per day", "1 in-progress swap at a time", "AI guest-post writing", "Verified backlink exchange"],
  pro: ["Match sites with up to 50K monthly visits", "50 swipes per day", "5 matches per day", "3 in-progress swaps at once", "Everything in Free", "Priority in the pool"],
  premium: ["Match sites of any traffic level", "Unlimited swipes", "10 matches per day", "6 in-progress swaps at once", "Everything in Pro", "Top-tier reputation boost"],
};
const ICONS = { free: Zap, pro: Zap, premium: Crown };

export default function Pricing() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [params] = useSearchParams();
  const [billing, setBilling] = useState("monthly");

  // Onboarding: brand-new user who hasn't picked a plan yet and has no site.
  const onboarding = !!user && !user.plan_chosen;

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
    if (params.get("canceled")) toast.info("Checkout canceled — no charge was made.");
    // eslint-disable-next-line
  }, []);

  const current = user?.plan || "free";

  const upgrade = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(null);
    }
  };

  const continueFree = async () => {
    setBusy("free");
    try {
      await api.post("/billing/continue-free");
      await refresh();
      toast.success("You're on the Free plan — add your site to enter the pool.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(null);
    }
  };

  const manage = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground px-6 py-14" data-testid="pricing-page">
      <div className="max-w-5xl mx-auto">
        {!onboarding && (
          <button onClick={() => nav(-1)} className="text-muted-foreground text-sm flex items-center gap-1.5 hover:text-foreground mb-8" data-testid="pricing-back">
            <ArrowLeft size={15} /> Back
          </button>
        )}
        <div className="text-center">
          <div className="font-mono text-xs tracking-[0.22em] text-primary uppercase">{onboarding ? "Step 1 of 2 · Choose a plan" : "Plans"}</div>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl tracking-tight mt-3">{onboarding ? "Pick a plan to get started." : "Swap more, rank faster."}</h1>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
            {onboarding
              ? "Choose how you want to swap. Start free and upgrade any time — or unlock more daily swaps and slots right away. You'll add your site next."
              : "Start free. Upgrade any time for more daily swaps, more concurrent swaps, and a bigger pipeline. Cancel whenever you like."}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 mt-8" data-testid="billing-toggle">
          <button onClick={() => setBilling("monthly")} data-testid="billing-monthly"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${billing === "monthly" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
            Monthly
          </button>
          <button onClick={() => setBilling("annual")} data-testid="billing-annual"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2 ${billing === "annual" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
            Annual
            <span className="text-[10px] font-mono rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 px-1.5 py-0.5">2 MONTHS FREE</span>
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {plans.map((p) => {
            const Icon = ICONS[p.id] || Zap;
            const isCurrent = !onboarding && current === p.id;
            const highlight = p.id === "pro";
            const annual = billing === "annual";
            const lookup = annual ? p.annual_lookup_key : p.lookup_key;
            const price = annual ? p.annual_price : p.price;
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`relative rounded-2xl border p-6 flex flex-col ${highlight ? "border-primary/50 bg-primary/[0.04]" : "border-white/10 bg-zinc-900/50"}`}>
                {highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-white text-[10px] font-mono tracking-widest px-3 py-1">POPULAR</div>}
                <div className="flex items-center gap-2">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center border ${p.id === "premium" ? "border-amber-500/30 bg-amber-500/10 text-amber-400" : "border-primary/25 bg-primary/10 text-primary"}`}><Icon size={17} /></div>
                  <div className="font-serif font-semibold text-xl">{p.name}</div>
                </div>
                <div className="mt-5">
                  <span className="font-serif font-bold text-4xl">${(price / 100).toFixed(price % 100 ? 2 : 0)}</span>
                  <span className="text-muted-foreground text-sm">{p.price ? (annual ? "/yr" : "/mo") : " forever"}</span>
                  {annual && p.price > 0 && (
                    <div className="text-[11px] text-emerald-400 mt-1 font-mono">2 months free · ${(p.price / 100).toFixed(2)}/mo billed yearly</div>
                  )}
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {FEATURES[p.id].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {onboarding ? (
                    p.price === 0 ? (
                      <button onClick={continueFree} disabled={busy === "free"} data-testid="plan-continue-free"
                        className="w-full rounded-full border border-white/15 bg-white/5 text-foreground hover:bg-white/10 py-2.5 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-50">
                        {busy === "free" ? <Loader2 size={15} className="animate-spin inline" /> : "Continue with Free"}
                      </button>
                    ) : (
                      <button onClick={() => upgrade(lookup)} disabled={busy === lookup} data-testid={`plan-upgrade-${p.id}`}
                        className={`w-full rounded-full py-2.5 text-sm font-semibold active:scale-95 transition-transform ${highlight ? "bg-primary text-white hover:bg-blue-600" : "bg-amber-500 text-white hover:bg-amber-600"} disabled:opacity-50`}>
                        {busy === lookup ? <Loader2 size={15} className="animate-spin inline" /> : `Start with ${p.name}`}
                      </button>
                    )
                  ) : isCurrent ? (
                    <button disabled data-testid={`plan-current-${p.id}`}
                      className="w-full rounded-full border border-white/10 bg-white/5 text-muted-foreground py-2.5 text-sm font-medium cursor-default">
                      Your current plan
                    </button>
                  ) : p.price === 0 ? (
                    <button disabled className="w-full rounded-full border border-white/10 bg-white/5 text-muted-foreground py-2.5 text-sm cursor-default">Free forever</button>
                  ) : (
                    <button onClick={() => upgrade(lookup)} disabled={busy === lookup} data-testid={`plan-upgrade-${p.id}`}
                      className={`w-full rounded-full py-2.5 text-sm font-semibold active:scale-95 transition-transform ${highlight ? "bg-primary text-white hover:bg-blue-600" : "bg-amber-500 text-white hover:bg-amber-600"} disabled:opacity-50`}>
                      {busy === lookup ? <Loader2 size={15} className="animate-spin inline" /> : `Upgrade to ${p.name}`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!onboarding && current !== "free" && (
          <div className="text-center mt-8">
            <button onClick={manage} disabled={busy === "portal"} data-testid="manage-billing"
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
              <Settings size={14} /> Manage billing or cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
